import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SideBar } from '../../layout';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import ResponsiveContainer from '../../layout/Container';
import DynamicSnackbar from '../../common/Snackbar';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SFTPService from '../../../services/SFTPService';
import CompanyService from '../../../services/CompanyService';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const HRJobSchedule = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [jobSchedule, setJobSchedule] = useState({
        COMPANY_ID: '',
        JOB_NAME: 'HR Data Import',
        MONDAY: false,
        TUESDAY: false,
        WEDNESDAY: false,
        THURSDAY: false,
        FRIDAY: false,
        SATURDAY: false,
        SUNDAY: false,
        SCHEDULE_TIME: new Date('2023-01-01T09:00:00'),
    });
    const [testPassword, setTestPassword] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [existingJobs, setExistingJobs] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [sftpConfigs, setSftpConfigs] = useState([]);

    useEffect(() => {
        // Fetch companies for dropdown
        const fetchCompanies = async () => {
            try {
                const companiesData = await CompanyService.fetchCompanies();
                setCompanies(companiesData);
            } catch (error) {
                console.error('Error fetching companies:', error);
                setSnackbarMessage('Failed to load companies');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        };

        // Fetch existing job schedules
        const fetchJobs = async () => {
            try {
                const jobsData = await SFTPService.fetchHRJobs();
                if (jobsData && jobsData.length > 0) {
                    setExistingJobs(jobsData);
                    // If we have jobs, select the first one by default
                    selectJob(jobsData[0]);
                }
            } catch (error) {
                console.error('Error fetching job schedules:', error);
            }
        };

        // Fetch SFTP configurations
        const fetchSFTPConfigs = async () => {
            try {
                const configData = await SFTPService.fetchSFTP();
                if (configData && configData.length > 0) {
                    setSftpConfigs(configData);
                }
            } catch (error) {
                console.error('Error fetching SFTP configurations:', error);
            }
        };

        fetchCompanies();
        fetchJobs();
        fetchSFTPConfigs();
    }, []);

    const selectJob = (job) => {
        setSelectedJob(job);
        
        // Convert time string to Date object
        const timeString = job.SCHEDULE_TIME;
        let timeDate = new Date('2023-01-01T09:00:00'); // Default time
        if (timeString) {
            try {
                // Parse "09:00:00" format to "2023-01-01T09:00:00"
                const [hours, minutes, seconds] = timeString.split(':').map(Number);
                timeDate = new Date(2023, 0, 1, hours, minutes, seconds);
            } catch (error) {
                console.error('Error parsing time:', error);
            }
        }
        
        setJobSchedule({
            ...job,
            SCHEDULE_TIME: timeDate
        });
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleChange = (e) => {
        const { name, checked, value, type } = e.target;
        setJobSchedule(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCompanyChange = (event) => {
        setJobSchedule(prev => ({
            ...prev,
            COMPANY_ID: event.target.value
        }));
    };

    const handleTimeChange = (newTime) => {
        setJobSchedule(prev => ({
            ...prev,
            SCHEDULE_TIME: newTime
        }));
    };

    const handleTestPasswordChange = (e) => {
        setTestPassword(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate at least one day is selected
        const anyDaySelected = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY']
            .some(day => jobSchedule[day]);
        
        if (!anyDaySelected) {
            setSnackbarMessage('Please select at least one day for scheduling');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        setLoading(true);
        
        try {
            let response;
            
            // Format time to time string (HH:MM:SS)
            const formattedTime = `${jobSchedule.SCHEDULE_TIME.getHours().toString().padStart(2, '0')}:${
                jobSchedule.SCHEDULE_TIME.getMinutes().toString().padStart(2, '0')}:${
                jobSchedule.SCHEDULE_TIME.getSeconds().toString().padStart(2, '0')}`;
            
            const dataToSend = {
                ...jobSchedule,
                SCHEDULE_TIME: formattedTime
            };
            
            if (selectedJob) {
                // Update existing job
                response = await SFTPService.updateHRJob(selectedJob.id, dataToSend);
                setSnackbarMessage('Job schedule updated successfully');
            } else {
                // Create new job
                response = await SFTPService.createHRJob(dataToSend);
                setSnackbarMessage('Job schedule created successfully');
                
                // Update jobs list
                const jobsData = await SFTPService.fetchHRJobs();
                setExistingJobs(jobsData);
                setSelectedJob(response);
            }
            
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error saving job schedule:', error);
            setSnackbarMessage('Failed to save job schedule');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleTestJob = async () => {
        if (!selectedJob) {
            setSnackbarMessage('Please save the job schedule before testing');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        if (!testPassword) {
            setSnackbarMessage('Password is required for testing the job');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        setTestLoading(true);
        
        try {
            const response = await SFTPService.testHRJob(selectedJob.id, testPassword);
            
            if (response.success) {
                setSnackbarMessage('Job test successful! Found files: ' + 
                    (response.files ? response.files.join(', ') : 'None'));
                setSnackbarSeverity('success');
            } else {
                setSnackbarMessage('Job test failed: ' + response.message);
                setSnackbarSeverity('error');
            }
        } catch (error) {
            console.error('Error testing job:', error);
            setSnackbarMessage('Job test failed: ' + (error.message || 'Unknown error'));
            setSnackbarSeverity('error');
        } finally {
            setTestLoading(false);
            setSnackbarOpen(true);
        }
    };

    // Check if there's a configured SFTP for the selected company or global SFTP config
    const hasSftpConfig = sftpConfigs.some(
        config => !jobSchedule.COMPANY_ID || !config.COMPANY_ID || config.COMPANY_ID === jobSchedule.COMPANY_ID
    );

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Breadcrumbs aria-label="breadcrumb">
                    <mui.Link underline="hover" color="inherit" href="/Dashboard" sx={{ display: 'flex', alignItems: 'center' }}>
                        <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                        Home
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/Dashboard">
                        Dashboard
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/hr-data-management">
                        HR Data
                    </mui.Link>
                    <mui.Typography color="text.primary">Job Scheduling</mui.Typography>
                </mui.Breadcrumbs>

                <SearchAppBar title="HR Job Scheduling" icon={<ScheduleIcon />} />
                <mui.Typography sx={{ marginTop: '20px' }} variant="subtitle2" gutterBottom>
                    Configure and manage automated HR data import jobs
                </mui.Typography>

                <Separator />

                {existingJobs.length > 0 && (
                    <mui.Box sx={{ mb: 4 }}>
                        <mui.Typography variant="h6" gutterBottom>
                            Existing Job Schedules
                        </mui.Typography>
                        <mui.TableContainer component={mui.Paper}>
                            <mui.Table size="small">
                                <mui.TableHead>
                                    <mui.TableRow>
                                        <mui.TableCell>Job Name</mui.TableCell>
                                        <mui.TableCell>Company</mui.TableCell>
                                        <mui.TableCell>Schedule</mui.TableCell>
                                        <mui.TableCell>Actions</mui.TableCell>
                                    </mui.TableRow>
                                </mui.TableHead>
                                <mui.TableBody>
                                    {existingJobs.map((job) => {
                                        // Get days as string
                                        const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY']
                                            .filter(day => job[day])
                                            .map(day => day.substring(0, 3))
                                            .join(', ');
                                            
                                        // Find company name
                                        const company = companies.find(c => c.id === job.COMPANY_ID);
                                        const companyName = company ? company.COMPANY_NAME : job.COMPANY_ID;
                                        
                                        return (
                                            <mui.TableRow 
                                                key={job.id}
                                                selected={selectedJob && selectedJob.id === job.id}
                                                onClick={() => selectJob(job)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <mui.TableCell>{job.JOB_NAME}</mui.TableCell>
                                                <mui.TableCell>{companyName}</mui.TableCell>
                                                <mui.TableCell>
                                                    {days} at {job.SCHEDULE_TIME}
                                                </mui.TableCell>
                                                <mui.TableCell>
                                                    <mui.Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            selectJob(job);
                                                        }}
                                                    >
                                                        Edit
                                                    </mui.Button>
                                                </mui.TableCell>
                                            </mui.TableRow>
                                        );
                                    })}
                                </mui.TableBody>
                            </mui.Table>
                        </mui.TableContainer>
                    </mui.Box>
                )}

                <mui.Button 
                    variant="contained" 
                    color="primary"
                    onClick={() => {
                        setSelectedJob(null);
                        setJobSchedule({
                            COMPANY_ID: '',
                            JOB_NAME: 'HR Data Import',
                            MONDAY: false,
                            TUESDAY: false,
                            WEDNESDAY: false,
                            THURSDAY: false,
                            FRIDAY: false,
                            SATURDAY: false,
                            SUNDAY: false,
                            SCHEDULE_TIME: new Date('2023-01-01T09:00:00'),
                        });
                    }}
                    sx={{ mb: 3 }}
                >
                    Create New Job Schedule
                </mui.Button>
                
                <mui.Button 
                    variant="outlined" 
                    color="secondary"
                    onClick={() => navigate('/interfaces/hr/job-status')}
                    sx={{ mb: 3, ml: 2 }}
                    startIcon={<AccessTimeIcon />}
                >
                    View Job Status & Control
                </mui.Button>

                <mui.Paper elevation={3} sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
                    <mui.Typography variant="h6" gutterBottom>
                        {selectedJob ? 'Edit Job Schedule' : 'Create Job Schedule'}
                    </mui.Typography>
                    
                    {!hasSftpConfig && (
                        <mui.Alert severity="warning" sx={{ mb: 3 }}>
                            You need to configure an SFTP connection for the selected company first.
                            <mui.Button 
                                size="small" 
                                color="inherit" 
                                onClick={() => navigate('/interfaces/hr/sftp')}
                                sx={{ ml: 2 }}
                            >
                                Configure SFTP
                            </mui.Button>
                        </mui.Alert>
                    )}
                    
                    <mui.Grid container spacing={3}>
                        <mui.Grid item xs={12} sm={6}>
                            <mui.FormControl fullWidth>
                                <mui.InputLabel id="company-select-label">Company</mui.InputLabel>
                                <mui.Select
                                    labelId="company-select-label"
                                    name="COMPANY_ID"
                                    value={jobSchedule.COMPANY_ID}
                                    label="Company"
                                    onChange={handleCompanyChange}
                                    disabled={!!selectedJob}
                                >
                                    <mui.MenuItem value="">
                                        <em>All Companies</em>
                                    </mui.MenuItem>
                                    {companies.map((company) => (
                                        <mui.MenuItem key={company.id} value={company.id}>
                                            {company.COMPANY_NAME}
                                        </mui.MenuItem>
                                    ))}
                                </mui.Select>
                                <mui.FormHelperText>
                                    Company for this job schedule (optional)
                                </mui.FormHelperText>
                            </mui.FormControl>
                        </mui.Grid>
                        
                        <mui.Grid item xs={12} sm={6}>
                            <mui.TextField
                                fullWidth
                                label="Job Name"
                                name="JOB_NAME"
                                value={jobSchedule.JOB_NAME}
                                onChange={handleChange}
                                placeholder="HR Data Import"
                            />
                        </mui.Grid>
                        
                        <mui.Grid item xs={12}>
                            <mui.Typography variant="subtitle1" gutterBottom>
                                Schedule Days
                            </mui.Typography>
                            <mui.FormGroup row>
                                {[
                                    { name: 'MONDAY', label: 'Monday' },
                                    { name: 'TUESDAY', label: 'Tuesday' },
                                    { name: 'WEDNESDAY', label: 'Wednesday' },
                                    { name: 'THURSDAY', label: 'Thursday' },
                                    { name: 'FRIDAY', label: 'Friday' },
                                    { name: 'SATURDAY', label: 'Saturday' },
                                    { name: 'SUNDAY', label: 'Sunday' },
                                ].map((day) => (
                                    <mui.FormControlLabel
                                        key={day.name}
                                        control={
                                            <mui.Checkbox
                                                checked={jobSchedule[day.name]}
                                                onChange={handleChange}
                                                name={day.name}
                                            />
                                        }
                                        label={day.label}
                                    />
                                ))}
                            </mui.FormGroup>
                        </mui.Grid>
                        
                        <mui.Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <TimePicker
                                    label="Schedule Time"
                                    value={jobSchedule.SCHEDULE_TIME}
                                    onChange={handleTimeChange}
                                    renderInput={(params) => (
                                        <mui.TextField {...params} fullWidth />
                                    )}
                                />
                            </LocalizationProvider>
                            <mui.FormHelperText>
                                Time when the job should run on selected days
                            </mui.FormHelperText>
                        </mui.Grid>
                        
                        <mui.Grid item xs={12}>
                            <mui.Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={loading || !hasSftpConfig}
                                startIcon={loading ? <mui.CircularProgress size={20} /> : null}
                            >
                                {selectedJob ? 'Update Job Schedule' : 'Create Job Schedule'}
                            </mui.Button>
                        </mui.Grid>
                    </mui.Grid>
                </mui.Paper>

                {selectedJob && (
                    <mui.Paper elevation={3} sx={{ p: 3, mt: 3, maxWidth: 800, mx: 'auto' }}>
                        <mui.Typography variant="h6" gutterBottom>
                            Test Job
                        </mui.Typography>
                        
                        <mui.Typography variant="body2" paragraph>
                            Test your job configuration to ensure it can access the SFTP server and find HR data files.
                        </mui.Typography>
                        
                        <mui.Grid container spacing={2} alignItems="center">
                            <mui.Grid item xs={12} sm={6}>
                                <mui.TextField
                                    fullWidth
                                    label="Password for Testing"
                                    value={testPassword}
                                    onChange={handleTestPasswordChange}
                                    type="password"
                                    required
                                />
                            </mui.Grid>
                            
                            <mui.Grid item xs={12} sm={6}>
                                <mui.Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleTestJob}
                                    disabled={testLoading}
                                    startIcon={testLoading ? <mui.CircularProgress size={20} color="inherit" /> : <PlayCircleOutlineIcon />}
                                >
                                    Test Job
                                </mui.Button>
                            </mui.Grid>
                        </mui.Grid>
                    </mui.Paper>
                )}
            </ResponsiveContainer>

            <DynamicSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={handleSnackbarClose}
            />
        </div>
    );

    return (
        <div>
            <SideBar mainContent={customMainContent} />
        </div>
    );
};

export default HRJobSchedule; 